import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';

type RenderMode = 'default' | 'simple';

interface RenderModeContextValue {
  renderMode: RenderMode;
  setRenderMode: (mode: RenderMode) => void;
  toggleRenderMode: () => void;
}

const RenderModeContext = createContext<RenderModeContextValue | null>(null);

interface RenderModeProviderProps {
  children: ReactNode;
  defaultMode?: RenderMode;
}

export function RenderModeProvider({
  children,
  defaultMode = 'default',
}: RenderModeProviderProps) {
  const [renderMode, setRenderMode] = useState<RenderMode>(defaultMode);

  const toggleRenderMode = useCallback(() => {
    setRenderMode((prev) => (prev === 'default' ? 'simple' : 'default'));
  }, []);

  return (
    <RenderModeContext.Provider
      value={{ renderMode, setRenderMode, toggleRenderMode }}
    >
      {children}
    </RenderModeContext.Provider>
  );
}

export function useRenderMode() {
  const context = useContext(RenderModeContext);
  // Return default values if not within provider (for standalone usage)
  if (!context) {
    return {
      renderMode: 'default' as RenderMode,
      setRenderMode: () => {},
      toggleRenderMode: () => {},
    };
  }
  return context;
}
